import { create } from 'zustand';
import { api } from '../services/api';

export interface PostInsights {
  id: string;
  caption: string | null;
  mediaType: 'IMAGE' | 'VIDEO' | 'CAROUSEL_ALBUM';
  permalink: string;
  timestamp: string;
  thumbnailUrl: string | null;
  likeCount: number;
  commentsCount: number;
  reach: number;
  saved: number;
  shares: number;
  engagementRate: number;
  ageInDays: number;
  ageInHours: number;
  reachPerDay: number;
  engagementsPerDay: number;
  // Early-stage metrics
  reachVelocity: number;
  engagementVelocity: number;
  earlyPerformanceScore: number;
  performanceCategory: 'exceptional' | 'above_average' | 'average' | 'below_average' | 'new';
  // Quality Score
  qualityScore: number;
  weightedEngagement: number;
}

export interface InsightsSummary {
  totalPosts: number;
  totalReach: number;
  totalEngagements: number;
  totalSaves: number;
  totalShares: number;
  averageEngagementRate: number;
  byType: {
    [key: string]: {
      count: number;
      avgEngagementRate: number;
      avgReach: number;
      totalReach: number;
    };
  };
  topByEngagementRate: PostInsights[];
  topByReach: PostInsights[];
  topBySaves: PostInsights[];
  recentTrend: 'up' | 'down' | 'stable';
  recentAvgEngagementRate: number;
  // Benchmarks for early-stage comparison
  benchmarks: {
    avgReachFirst24h: number;
    avgReachFirst48h: number;
    avgReachFirst72h: number;
    avgEngagementFirst24h: number;
    avgReachVelocity: number;
    avgEngagementVelocity: number;
  };
  earlyStagePerformers: PostInsights[];
  potentialBreakouts: PostInsights[];
}

export interface InsightsAccount {
  username: string;
  followersCount: number;
  mediaCount: number;
}

interface InsightsState {
  posts: PostInsights[];
  summary: InsightsSummary | null;
  account: InsightsAccount | null;
  fetchedAt: string | null;
  isLoading: boolean;
  error: string | null;
  sortBy: 'engagementRate' | 'reach' | 'saved' | 'timestamp' | 'likeCount' | 'qualityScore';
  sortOrder: 'asc' | 'desc';
  filterType: 'all' | 'IMAGE' | 'VIDEO' | 'CAROUSEL_ALBUM';
}

interface InsightsActions {
  fetchInsights: (limit?: number) => Promise<void>;
  fetchSummary: () => Promise<void>;
  setSortBy: (sortBy: InsightsState['sortBy']) => void;
  setSortOrder: (sortOrder: InsightsState['sortOrder']) => void;
  setFilterType: (filterType: InsightsState['filterType']) => void;
  getSortedPosts: () => PostInsights[];
  reset: () => void;
}

const initialState: InsightsState = {
  posts: [],
  summary: null,
  account: null,
  fetchedAt: null,
  isLoading: false,
  error: null,
  sortBy: 'engagementRate',
  sortOrder: 'desc',
  filterType: 'all',
};

export const useInsightsStore = create<InsightsState & InsightsActions>((set, get) => ({
  ...initialState,

  fetchInsights: async (limit = 50) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.get<{
        success: boolean;
        posts: PostInsights[];
        summary: InsightsSummary;
        account: InsightsAccount;
        fetchedAt: string;
        error?: string;
      }>(`/insights/instagram?limit=${limit}`);

      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch insights');
      }

      set({
        posts: response.posts,
        summary: response.summary,
        account: response.account,
        fetchedAt: response.fetchedAt,
        error: null,
      });
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Unbekannter Fehler' });
    } finally {
      set({ isLoading: false });
    }
  },

  fetchSummary: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.get<{
        success: boolean;
        summary: InsightsSummary;
        account: InsightsAccount;
        fetchedAt: string;
        error?: string;
      }>('/insights/summary');

      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch summary');
      }

      set({
        summary: response.summary,
        account: response.account,
        fetchedAt: response.fetchedAt,
        error: null,
      });
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Unbekannter Fehler' });
    } finally {
      set({ isLoading: false });
    }
  },

  setSortBy: (sortBy) => set({ sortBy }),
  setSortOrder: (sortOrder) => set({ sortOrder }),
  setFilterType: (filterType) => set({ filterType }),

  getSortedPosts: () => {
    const { posts, sortBy, sortOrder, filterType } = get();

    let filtered = posts;
    if (filterType !== 'all') {
      filtered = posts.filter((p) => p.mediaType === filterType);
    }

    const sorted = [...filtered].sort((a, b) => {
      let aVal: number;
      let bVal: number;

      switch (sortBy) {
        case 'engagementRate':
          aVal = a.engagementRate;
          bVal = b.engagementRate;
          break;
        case 'reach':
          aVal = a.reach;
          bVal = b.reach;
          break;
        case 'saved':
          aVal = a.saved;
          bVal = b.saved;
          break;
        case 'likeCount':
          aVal = a.likeCount;
          bVal = b.likeCount;
          break;
        case 'qualityScore':
          aVal = a.qualityScore;
          bVal = b.qualityScore;
          break;
        case 'timestamp':
          aVal = new Date(a.timestamp).getTime();
          bVal = new Date(b.timestamp).getTime();
          break;
        default:
          aVal = a.engagementRate;
          bVal = b.engagementRate;
      }

      return sortOrder === 'desc' ? bVal - aVal : aVal - bVal;
    });

    return sorted;
  },

  reset: () => set(initialState),
}));
